import { makeAutoObservable, runInAction } from 'mobx'
import { truthy } from 'shared/lib'
import { SendFromStore } from 'widgets/SendFrom'
import { EnrollmentFilters } from 'widgets/ContactEnrollment'
import type { IEnrollmentActions } from 'widgets/ContactEnrollment/store/types'
import { EnrollByWebhookStore } from 'widgets/EnrollByWebhook/store/EnrollByWebhookStore.ts'
import { formatWidgetMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import type { TriggerSectionStore } from './triggerSectionStore'

const stepsTitleMap = {
  howTriggered: 'How should this agent be triggered?',
  whichInbox: 'Which inbox should the agent use to start conversations?',
  contactsEnroll: 'How should contacts be enrolled?',
  reEnroll: 'Allow contacts to re-enroll after completion?',
}

type IStepKey = keyof typeof stepsTitleMap

const stepsMap = {
  onlyManual: ['howTriggered', 'whichInbox'] satisfies IStepKey[],
  withAutomatically: [
    'howTriggered',
    'whichInbox',
    'contactsEnroll',
    'reEnroll',
  ] satisfies IStepKey[],
  withWebhook: ['howTriggered', 'whichInbox', 'reEnroll'] satisfies IStepKey[],
}

export const RE_ENROLL_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export class TriggerWidgetStore {
  focusedIndex = 0
  isSkipped = false
  localRunAutomatically: boolean
  readonly enrollByWebhookStore: EnrollByWebhookStore
  localEnrollmentFilters = EnrollmentFilters.Empty()
  localEnrollmentExcludeFilters = EnrollmentFilters.Empty()
  readonly localSendFromStore: SendFromStore

  constructor(readonly sectionStore: TriggerSectionStore, readonly chatStore: ChatStore) {
    this.localRunAutomatically = sectionStore.runAutomatically
    this.localSendFromStore = new SendFromStore(sectionStore.entities, { includeRCS: true })
    this.localSendFromStore.init()
    this.enrollByWebhookStore = new EnrollByWebhookStore({
      isActive: sectionStore.enrollByWebhookStore.isActive,
    })
    if (this.localRunAutomatically) {
      this.stepKeys = stepsMap['withAutomatically']
    } else if (this.enrollByWebhookStore.isActive) {
      this.stepKeys = stepsMap['withWebhook']
    } else {
      this.stepKeys = stepsMap['onlyManual']
    }
    makeAutoObservable(this)
  }

  currentStepKey: IStepKey = 'howTriggered'
  stepKeys: IStepKey[]

  get step() {
    return this.stepKeys.indexOf(this.currentStepKey) + 1
  }

  get totalSteps() {
    return this.stepKeys.length
  }

  get stepHeader() {
    return stepsTitleMap[this.currentStepKey]
  }

  get isLastStep() {
    return this.step === this.totalSteps
  }

  get currentListLength() {
    switch (this.currentStepKey) {
      case 'howTriggered':
        return 3
      case 'reEnroll':
        return RE_ENROLL_OPTIONS.length
      default:
        return 0
    }
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    return Math.min(this.focusedIndex, this.currentListLength - 1)
  }

  get isSubmitDisabled() {
    return this.localSendFromStore.selectedDropdownItem === null
  }

  get isNextDisabled() {
    if (this.currentStepKey === 'whichInbox')
      return this.localSendFromStore.selectedDropdownItem === null
    if (this.currentStepKey === 'contactsEnroll')
      return !this.localEnrollmentFilters.allSize && !this.localEnrollmentExcludeFilters.allSize
    return false
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    this.focusedIndex = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
  }

  private advanceStep = () => {
    const nextIndex = this.stepKeys.indexOf(this.currentStepKey) + 1
    this.currentStepKey = this.stepKeys[nextIndex]
    this.focusedIndex = 0
  }

  goBack = () => {
    const prevIndex = this.stepKeys.indexOf(this.currentStepKey) - 1
    this.currentStepKey = this.stepKeys[prevIndex]
  }

  handleNext = () => {
    this.advanceStep()
  }

  handleSelect = () => {
    if (this.currentStepKey === 'howTriggered') {
      if (this.focusedIndex === 1) {
        this.localRunAutomatically = !this.localRunAutomatically
      }
      if (this.focusedIndex === 2) {
        this.enrollByWebhookStore.setActive(!this.enrollByWebhookStore.isActive)
      }
      if (this.localRunAutomatically) {
        this.stepKeys = stepsMap['withAutomatically']
      } else if (this.enrollByWebhookStore.isActive) {
        this.stepKeys = stepsMap['withWebhook']
      } else {
        this.stepKeys = stepsMap['onlyManual']
      }
    }
  }

  selectAt = (index: number) => {
    this.setFocusedIndex(index)
    this.handleSelect()
  }

  getInboxName = () => {
    const selectedInboxItem = this.localSendFromStore.selectedDropdownItem

    if (!selectedInboxItem) return 'Skip'
    const data = selectedInboxItem.data
    const mainLabel = data?.parentLabel || selectedInboxItem.label
    const subLabel =
      data?.smart_option_label || data?.mergeField?.name || data?.number?.formatted_number
    return subLabel ? `${mainLabel}: ${subLabel}` : mainLabel
  }

  get submitMessage(): string {
    if (this.isSkipped) {
      const steps = this.stepKeys.slice(0, this.step)
      const skippedPairs: Array<{ q: string; a: string }> = steps.map((key) => ({
        q: stepsTitleMap[key],
        a: 'Skip',
      }))
      return formatWidgetMessage(skippedPairs)
    }

    const pairs: Array<{ q: string; a: string }> = this.stepKeys.map((key) => {
      const q = stepsTitleMap[key]
      const getAnswer = (): string => {
        switch (key) {
          case 'howTriggered': {
            return [
              'Manually',
              this.localRunAutomatically && 'Automatically',
              this.enrollByWebhookStore.isActive && 'By webhook',
            ]
              .filter(truthy)
              .join(', ')
          }
          case 'whichInbox': {
            return this.getInboxName()
          }
          case 'contactsEnroll': {
            return !!this.localEnrollmentFilters.allSize
              ? `${this.localEnrollmentFilters.allSize} set of filters selected`
              : 'Skip'
          }
          case 'reEnroll': {
            return RE_ENROLL_OPTIONS[this.safeIndex]?.id === 'yes' ? 'Yes' : 'No'
          }
          default: {
            return ''
          }
        }
      }
      return {
        q,
        a: getAnswer(),
      }
    })

    return formatWidgetMessage(pairs)
  }

  editLocalEnrollment = () => {
    const filters = this.localEnrollmentFilters.clone()
    const excludeFilters = this.localEnrollmentExcludeFilters.clone()

    const cancelFilters = () => Promise.resolve(true)

    const confirmFilters: IEnrollmentActions['confirm'] = async ({ filters, excludeFilters }) => {
      runInAction(() => {
        this.localEnrollmentFilters = filters
        this.localEnrollmentExcludeFilters = excludeFilters
      })
      return true
    }

    this.sectionStore.enrollmentStore.openModal({
      title: 'Enroll contacts',
      allowEmptyCount: true,
      filters: filters,
      excludeFilters: excludeFilters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
    })
  }

  handleSubmit = () => {
    const message = this.submitMessage
    const shouldSetReEnroll = this.currentStepKey === 'reEnroll'
    const reEnroll = shouldSetReEnroll ? RE_ENROLL_OPTIONS[this.safeIndex].id === 'yes' : false
    const enrollmentFilters = this.localEnrollmentFilters
    const enrollmentExcludeFilters = this.localEnrollmentExcludeFilters
    const selectedDropdownItem = this.localSendFromStore.selectedDropdownItem
    const runAutomatically = this.localRunAutomatically

    this.chatStore.sendMessage(message, () => {
      this.chatStore.applySectionUpdateSilently('trigger', () => {
        this.sectionStore.setRunAutomatically(runAutomatically)
        if (shouldSetReEnroll) {
          this.sectionStore.setAllowReEntering(reEnroll)
        }
        this.sectionStore.sendFromStore.setSelectedDropdownItem(selectedDropdownItem)
        this.sectionStore.enrollmentFilters = enrollmentFilters
        this.sectionStore.enrollmentExcludeFilters = enrollmentExcludeFilters
        this.sectionStore.enrollByWebhookStore.setActive(this.enrollByWebhookStore.isActive)
      })
    })
  }

  handleClose = () => {
    this.isSkipped = true
    const message = this.submitMessage
    this.chatStore.sendMessage(message)
  }

  reset = () => {
    this.currentStepKey = 'howTriggered'
    this.focusedIndex = 0
    this.isSkipped = false
    this.localRunAutomatically = this.sectionStore.runAutomatically
    this.localEnrollmentFilters = EnrollmentFilters.Empty()
    this.localEnrollmentExcludeFilters = EnrollmentFilters.Empty()
  }
}
